import {useEffect} from 'react';
import {reaction} from 'mobx';

import {useSearchDispatch} from '../context/SearchContext';
import {queryClient} from '../queries/queryClient';
import {searchKeys} from '../queries/search/keys';
import {useStores} from '../stores/use-stores';

/**
 * Bridges MobX store selection to SearchContext using reaction().
 *
 * Uses MobX reaction() instead of useEffect + observable read because:
 * - No observer() wrapper needed on the host component
 * - Reacts to MobX changes directly (not dependent on React render cycle)
 * - Tracks currentStoreId locally (this hook is the sole writer of storeId)
 */
export function useStoreSync() {
  const dispatch = useSearchDispatch();
  const {shopSelectionStore} = useStores();

  useEffect(() => {
    let currentStoreId: string | null = null;

    const dispose = reaction(
      () => shopSelectionStore.selectedStore?.id ?? null,
      mflStoreId => {
        if (mflStoreId && mflStoreId !== currentStoreId) {
          const previousStoreId = currentStoreId;
          dispatch({type: 'SET_STORE_ID', storeId: mflStoreId});
          if (previousStoreId) {
            queryClient.removeQueries({
              queryKey: searchKeys.store(previousStoreId),
            });
          }
          currentStoreId = mflStoreId;
        }
      },
      {fireImmediately: true},
    );

    return dispose;
  }, [dispatch, shopSelectionStore]);
}
